/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  ReactNode,
  useMemo,
  useRef,
} from 'react';
import { useGlobalCallProviderContext } from 'src/contexts/global-call-provider-context';
import { useCallCallbacks } from 'src/contexts/global-call-provider-context';
import { initialState, speedDialerReducer } from '../reducers/speed-dialer';
import {
  SpeedDialerStatus,
  SpeedDialerQueueItem,
  SpeedDialerAction,
} from '../types/speed-dialer.type';
import { isValidPhoneNumber } from 'src/modules/shared/utils/validation/phone.util';
import Loading from 'src/modules/shared/components/atoms/loading';
import { useNumberSetting } from 'src/hooks/settings/settings';
import { SettingName } from 'src/api/generated/models/SettingName';

interface SpeedDialerContextValue {
  // State
  status: SpeedDialerStatus;
  queue: SpeedDialerQueueItem[];
  currentConversationId?: string;
  currentPhoneNumber?: string;
  currentPhoneIndex: number;
  currentAttempts: number;
  isSessionActive: boolean;
  maxAttempts: number;

  // Actions
  startSession: (queue: SpeedDialerQueueItem[]) => void;
  pauseSession: () => void;
  resumeSession: () => void;
  stopSession: () => void;
  processNextCall: () => void;
  updatePhoneNumber: (newPhone: string) => void;
  skipCall: () => void;
}

const SpeedDialerContext = createContext<SpeedDialerContextValue | undefined>(
  undefined
);

export interface SpeedDialerProviderProps {
  children: ReactNode;
}

/**
 * Hook to automatically process the next call when conditions are met.
 * Will trigger the next call when:
 * 1. The session is active
 * 2. There are items in the queue
 * 3. No current phone number is set
 */
const useAutoProcessNextCall = (
  status: SpeedDialerStatus,
  queue: SpeedDialerQueueItem[],
  currentPhoneNumber: string | undefined,
  processNextCall: () => void
) => {
  useEffect(() => {
    if (status === 'active' && queue.length > 0 && !currentPhoneNumber) {
      processNextCall();
    }
  }, [status, currentPhoneNumber, queue, processNextCall]);
};

/**
 * Hook to automatically initiate a call when a call is processed.
 * Will trigger the call when:
 * 1. The status is 'processing_call'
 * 2. We have a valid phone number and conversation ID
 * 3. No call has been attempted yet for this number
 */
const useAutoInitiateCall = (
  status: SpeedDialerStatus,
  currentPhoneNumber: string | undefined,
  currentConversationId: string | undefined,
  dispatch: React.Dispatch<SpeedDialerAction>,
  callAttemptedRef: React.MutableRefObject<boolean>
) => {
  const { provider: callProvider, loading: providerLoading } =
    useGlobalCallProviderContext();

  useEffect(() => {
    if (providerLoading || !callProvider) {
      return;
    }

    if (
      status === 'processing_call' &&
      currentPhoneNumber &&
      currentConversationId &&
      !callAttemptedRef.current
    ) {
      callAttemptedRef.current = true;

      if (!isValidPhoneNumber(currentPhoneNumber)) {
        return;
      }

      callProvider.callActions
        .startCall({ phoneNumber: currentPhoneNumber })
        .catch((error) => {
          console.error('Failed to start call:', error);
          dispatch({ type: 'CALL_ENDED' });
        });
    } else if (status !== 'processing_call') {
      callAttemptedRef.current = false;
    }
  }, [
    currentPhoneNumber,
    currentConversationId,
    status,
    providerLoading,
    callProvider,
    dispatch,
    callAttemptedRef,
  ]);
};

export const SpeedDialerProvider: React.FC<SpeedDialerProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(speedDialerReducer, initialState);
  const callAttemptedRef = useRef(false);
  const pauseTriggeredRef = useRef(false);

  const { provider: callProvider, loading: providerLoading } =
    useGlobalCallProviderContext();

  const { setting: configuredMaxAttempts, loading: maxAttemptsLoading } =
    useNumberSetting(SettingName.SPEED_DIALER_MAX_ATTEMPTS);

  const startSession = useCallback(
    (queue: SpeedDialerQueueItem[]) => {
      const fallbackMaxAttempts = 1;
      const isConfiguredMaxAttempts =
        !maxAttemptsLoading &&
        configuredMaxAttempts &&
        configuredMaxAttempts > 0;

      const normalizedMaxAttempts = isConfiguredMaxAttempts
        ? configuredMaxAttempts
        : fallbackMaxAttempts;
      dispatch({
        type: 'START_SESSION',
        payload: { queue, maxAttempts: normalizedMaxAttempts },
      });
    },
    [dispatch, configuredMaxAttempts, maxAttemptsLoading]
  );

  const pauseSession = useCallback(() => {
    pauseTriggeredRef.current = true;
    if (!providerLoading && callProvider) {
      callProvider.callActions.endCall();
    }
    dispatch({ type: 'PAUSE_SESSION' });
  }, [providerLoading, callProvider, dispatch]);

  const resumeSession = useCallback(() => {
    callAttemptedRef.current = false;
    dispatch({ type: 'RESUME_SESSION' });
  }, [dispatch]);

  const updatePhoneNumber = useCallback(
    (newPhone: string) => {
      dispatch({ type: 'UPDATE_PHONE_NUMBER', payload: { newPhone } });
      callAttemptedRef.current = false;
    },
    [dispatch]
  );

  const stopSession = useCallback(() => {
    if (!providerLoading && callProvider) {
      callProvider.callActions.endCall();
    }

    dispatch({ type: 'STOP_SESSION' });
  }, [callProvider, providerLoading, dispatch]);

  const processNextCall = useCallback(() => {
    dispatch({ type: 'PROCESS_NEXT_CALL' });
  }, [dispatch]);

  const skipCall = useCallback(() => {
    dispatch({ type: 'SKIP_CALL' });
  }, [dispatch]);

  const handleCallStarted = useCallback(() => {
    if (!state.currentConversationId || !state.currentPhoneNumber) {
      console.error('No conversation ID or phone number found');
      return;
    }

    dispatch({
      type: 'CALL_STARTED',
      payload: {
        conversationId: state.currentConversationId,
        phoneNumber: state.currentPhoneNumber,
      },
    });
  }, [state.currentConversationId, state.currentPhoneNumber]);

  const handleCallEnded = useCallback(() => {
    if (pauseTriggeredRef.current) {
      pauseTriggeredRef.current = false;
      return;
    }

    callAttemptedRef.current = false;
    dispatch({ type: 'CALL_ENDED' });
  }, [dispatch]);

  useCallCallbacks({
    onCallStarted: handleCallStarted,
    onCallEnded: handleCallEnded,
  });

  useAutoProcessNextCall(
    state.status,
    state.queue,
    state.currentPhoneNumber,
    processNextCall
  );

  useAutoInitiateCall(
    state.status,
    state.currentPhoneNumber,
    state.currentConversationId,
    dispatch,
    callAttemptedRef
  );
  const isSessionActive = useMemo(
    () => state.status !== 'idle',
    [state.status]
  );

  const contextValue: SpeedDialerContextValue = {
    status: state.status,
    queue: state.queue,
    currentConversationId: state.currentConversationId,
    currentPhoneNumber: state.currentPhoneNumber,
    currentPhoneIndex: state.currentPhoneIndex,
    currentAttempts: state.currentAttempts,
    maxAttempts: state.maxAttempts,
    startSession,
    pauseSession,
    resumeSession,
    stopSession,
    processNextCall,
    isSessionActive,
    updatePhoneNumber,
    skipCall,
  };

  if (providerLoading || maxAttemptsLoading || !callProvider) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <SpeedDialerContext.Provider value={contextValue}>
      {children}
    </SpeedDialerContext.Provider>
  );
};

export const useSpeedDialer = (): SpeedDialerContextValue => {
  const context = useContext(SpeedDialerContext);
  if (!context) {
    throw new Error('useSpeedDialer must be used within a SpeedDialerProvider');
  }
  return context;
};
